/**
 * Photo evidence progress, derived from the shot profiles rather than a constant.
 *
 * The required shot count depends on breaker type: on MY02 today an ACB needs
 * 52 photos and an MCCB 43, because an ACB has six states to evidence and an
 * MCCB three. Those figures have already changed once — they are deliberately
 * not written down anywhere but the shot profiles, so a change to the model
 * moves every count with it instead of leaving a constant lying about.
 */

import type { BreakerType, CapturedShot, ShotProfile, ShotGroup } from './types.js';

export interface GroupProgress {
  group: ShotGroup;
  required: number;
  captured: number;
  missing: string[];
}

export interface ShotProgress {
  breakerType: BreakerType;
  required: number;
  captured: number;
  /** Shot codes still outstanding, in capture order. */
  missing: string[];
  /** Photos present that no profile asks for — usually a retake or a mis-tag. */
  unexpected: string[];
  complete: boolean;
  byGroup: GroupProgress[];
}

export function profilesFor(
  profiles: readonly ShotProfile[],
  breakerType: BreakerType,
): ShotProfile[] {
  return profiles
    .filter((p) => p.breakerType === breakerType && p.required)
    .sort((a, b) => a.seq - b.seq);
}

export function requiredShotCount(
  profiles: readonly ShotProfile[],
  breakerType: BreakerType,
): number {
  return profilesFor(profiles, breakerType).length;
}

/**
 * Progress for one breaker.
 *
 * A shot counts as captured only when a photo exists for it; a queued capture
 * with no image is not evidence. Duplicates of the same shot code count once,
 * so a retake never inflates the tally past 100%.
 */
export function shotProgress(
  profiles: readonly ShotProfile[],
  breakerType: BreakerType,
  captured: readonly CapturedShot[],
): ShotProgress {
  const required = profilesFor(profiles, breakerType);
  const requiredCodes = new Set(required.map((p) => p.shotCode));

  const capturedCodes = new Set(
    captured.filter((c) => c.photoUrl !== undefined && c.photoUrl !== '').map((c) => c.shotCode),
  );

  const missing = required.filter((p) => !capturedCodes.has(p.shotCode)).map((p) => p.shotCode);
  const unexpected = [...capturedCodes].filter((c) => !requiredCodes.has(c)).sort();

  const groups: ShotGroup[] = ['Phase Rotation & Earth Loop', 'Meter Verification', 'CB Status'];
  const byGroup: GroupProgress[] = groups
    .map((group) => {
      const inGroup = required.filter((p) => p.shotGroup === group);
      const got = inGroup.filter((p) => capturedCodes.has(p.shotCode));
      return {
        group,
        required: inGroup.length,
        captured: got.length,
        missing: inGroup.filter((p) => !capturedCodes.has(p.shotCode)).map((p) => p.shotCode),
      };
    })
    .filter((g) => g.required > 0);

  const capturedCount = required.length - missing.length;

  return {
    breakerType,
    required: required.length,
    captured: capturedCount,
    missing,
    unexpected,
    complete: missing.length === 0 && required.length > 0,
    byGroup,
  };
}
